import { Howl, Howler } from 'howler';
import type { GameSettings } from '../app/types';
import audioData from '../data/audio.json';
import type { AssetLoader } from './AssetLoader';
import { debugLogger } from './DebugLogger';

export type AudioChannel = 'bgm' | 'se' | 'voice';
export type AudioUnlockSource = 'pointer' | 'keyboard' | 'debug';

export interface AudioPlayResult {
  id: string;
  channel: AudioChannel;
  played: boolean;
  placeholder: boolean;
  interrupted?: boolean;
  suppressed?: boolean;
  suppressedReason?: 'cooldown' | 'overlap';
}

export interface AudioDebugState {
  currentBgmId: string | null;
  currentBgmPlaceholder: boolean;
  muted: boolean;
  audioUnlockAttemptCount: number;
  audioContextResumeCount: number;
  audioContextState: string | null;
  lastAudioUnlockSource: AudioUnlockSource | null;
  masterVolume: number;
  bgmVolume: number;
  seVolume: number;
  voiceVolume: number;
  activeBgmHowlCount: number;
  activeBgmPlayingCount: number;
  bgmRequestCount: number;
  bgmStartCount: number;
  bgmPlaceholderCount: number;
  bgmPlayErrorCount: number;
  bgmUnlockRetryCount: number;
  lastBgmRequestId: string | null;
  lastBgmStartedId: string | null;
  lastBgmPlayErrorId: string | null;
  cachedSeCount: number;
  seRequestCount: number;
  sePlaybackCount: number;
  seSuppressedCount: number;
  lastSeRequestId: string | null;
  lastSePlayedId: string | null;
}

export interface PlayBgmOptions {
  fadeMs?: number;
  loop?: boolean;
  onEnd?: (id: string) => void;
}

export interface PlaySeOptions {
  volume?: number;
  cooldownMs?: number;
  allowOverlap?: boolean;
}

type PersistAudioSettings = (settings: Partial<GameSettings>) => void;

interface ActiveBgm {
  id: string;
  howl: Howl | null;
  soundId: number | null;
  placeholder: boolean;
}

type HowlWithStopFade = Howl & {
  _stopFade?: (id: number) => Howl;
};

interface SePlaybackPolicy {
  volume: number;
  cooldownMs: number;
  allowOverlap: boolean;
}

const DEFAULT_MASTER_VOLUME = 1;
const DEFAULT_BGM_FADE_MS = 500;
const FADE_CLEANUP_GRACE_MS = 100;
const DEFAULT_SE_POLICY: SePlaybackPolicy = {
  volume: 1,
  cooldownMs: 0,
  allowOverlap: true
};
const SE_POLICIES: Record<string, Partial<SePlaybackPolicy>> = {
  phone_call: {
    cooldownMs: 300,
    allowOverlap: false
  },
  radio_noise: {
    volume: 0.4,
    cooldownMs: 800,
    allowOverlap: false
  },
  evidence_get: {
    cooldownMs: 300,
    allowOverlap: false
  },
  notice_paper: {
    volume: 0.52,
    cooldownMs: 500,
    allowOverlap: false
  },
  zero_fail: {
    volume: 0.58,
    cooldownMs: 520,
    allowOverlap: false
  },
  clock_reverse: {
    volume: 0.54,
    cooldownMs: 700,
    allowOverlap: false
  },
  newspaper_transition: {
    volume: 0.48,
    cooldownMs: 560,
    allowOverlap: false
  },
  konai_arrival: {
    cooldownMs: 620,
    allowOverlap: false
  },
  unresolved_stamp: {
    cooldownMs: 980,
    allowOverlap: false
  },
  choice_select: {
    cooldownMs: 160,
    allowOverlap: false
  },
  ui_click: {
    cooldownMs: 120,
    allowOverlap: false
  }
};
const AUDIO_DEFAULT_VOLUMES = new Map(
  audioData.audio.map((track) => [track.id, normalizeAudioDefaultVolume(track.defaultVolume)])
);

export class AudioManager {
  private readonly seCache = new Map<string, Howl>();
  private readonly lastSePlayedAt = new Map<string, number>();
  private readonly bgmHowls = new Set<Howl>();
  private readonly pendingBgmUnlockRetries = new Map<Howl, () => void>();
  private currentBgm: ActiveBgm | null = null;
  private masterVolume: number;
  private bgmVolume: number;
  private seVolume: number;
  private voiceVolume: number;
  private muted = false;
  private bgmRequestToken = 0;
  private destroyed = false;
  private bgmRequestCount = 0;
  private bgmStartCount = 0;
  private bgmPlaceholderCount = 0;
  private bgmPlayErrorCount = 0;
  private bgmUnlockRetryCount = 0;
  private audioUnlockAttemptCount = 0;
  private audioContextResumeCount = 0;
  private lastAudioUnlockSource: AudioUnlockSource | null = null;
  private lastBgmRequestId: string | null = null;
  private lastBgmStartedId: string | null = null;
  private lastBgmPlayErrorId: string | null = null;
  private seRequestCount = 0;
  private sePlaybackCount = 0;
  private seSuppressedCount = 0;
  private lastSeRequestId: string | null = null;
  private lastSePlayedId: string | null = null;

  public constructor(
    private readonly assetLoader: AssetLoader,
    initialSettings: GameSettings,
    private readonly persistSettings?: PersistAudioSettings
  ) {
    this.masterVolume = clamp01(initialSettings.masterVolume);
    this.bgmVolume = clamp01(initialSettings.bgmVolume);
    this.seVolume = clamp01(initialSettings.seVolume);
    this.voiceVolume = clamp01(initialSettings.voiceVolume);
    Howler.volume(this.masterVolume);
    Howler.mute(this.muted);
  }

  public async playBgm(id: string, options: PlayBgmOptions = {}): Promise<AudioPlayResult> {
    this.bgmRequestCount += 1;
    this.lastBgmRequestId = id;
    const requestToken = ++this.bgmRequestToken;
    debugLogger.log('audio', 'playBgm request', {
      id,
      requestToken,
      fadeMs: options.fadeMs ?? DEFAULT_BGM_FADE_MS,
      loop: options.loop ?? true
    });

    if (this.currentBgm?.id === id && !this.currentBgm.placeholder) {
      if (this.canReuseCurrentBgm(this.currentBgm)) {
        debugLogger.log('audio', 'playBgm reuse current', { id });
        return { id, channel: 'bgm', played: true, placeholder: false };
      }

      debugLogger.log('audio', 'playBgm restart inactive current', { id });
      await this.stopCurrentBgm(0);
    }

    const fadeMs = normalizeDurationMs(options.fadeMs ?? DEFAULT_BGM_FADE_MS);
    const asset = await this.assetLoader.loadAudio({ id, kind: 'bgm' });
    if (!this.isLatestBgmRequest(requestToken)) {
      debugLogger.log('audio', 'playBgm interrupted after asset load', { id, requestToken });
      return createInterruptedBgmResult(id);
    }

    await this.stopCurrentBgm(fadeMs);
    if (!this.isLatestBgmRequest(requestToken)) {
      debugLogger.log('audio', 'playBgm interrupted after stop', { id, requestToken });
      return createInterruptedBgmResult(id);
    }

    if (asset.placeholder) {
      this.bgmPlaceholderCount += 1;
      this.currentBgm = {
        id,
        howl: null,
        soundId: null,
        placeholder: true
      };
      debugLogger.log('audio', 'playBgm placeholder', { id, url: asset.url });
      return { id, channel: 'bgm', played: false, placeholder: true };
    }

    const howl = new Howl({
      src: [asset.url],
      loop: options.loop ?? true,
      html5: true,
      volume: fadeMs > 0 ? 0 : this.getBgmPlaybackVolume(id)
    });
    this.bgmHowls.add(howl);

    let soundId: number | null = null;
    this.bindBgmLoadError(id, howl, () => soundId);
    this.bindBgmFadeIn(id, howl, () => soundId, fadeMs);
    this.bindBgmAutoplayRetry(id, howl, () => soundId);
    this.bindBgmEnd(id, howl, () => soundId, options.onEnd);

    let playedSoundId: number | string | null | undefined;
    try {
      playedSoundId = howl.play();
    } catch {
      this.bgmPlayErrorCount += 1;
      this.lastBgmPlayErrorId = id;
      this.unloadBgmHowl(howl, null);
      debugLogger.log('audio', 'playBgm play threw', { id });
      return { id, channel: 'bgm', played: false, placeholder: false };
    }

    if (typeof playedSoundId !== 'number') {
      this.bgmPlayErrorCount += 1;
      this.lastBgmPlayErrorId = id;
      this.unloadBgmHowl(howl, null);
      debugLogger.log('audio', 'playBgm play returned non numeric id', { id, playedSoundId });
      return { id, channel: 'bgm', played: false, placeholder: false };
    }

    soundId = playedSoundId;
    this.bgmStartCount += 1;
    this.lastBgmStartedId = id;

    this.currentBgm = {
      id,
      howl,
      soundId,
      placeholder: false
    };
    debugLogger.log('audio', 'playBgm started', {
      id,
      soundId,
      volume: this.getBgmPlaybackVolume(id)
    });

    return { id, channel: 'bgm', played: true, placeholder: false };
  }

  public async stopBgm(fadeMs = DEFAULT_BGM_FADE_MS): Promise<void> {
    this.bgmRequestToken += 1;
    debugLogger.log('audio', 'stopBgm request', {
      currentBgmId: this.currentBgm?.id ?? null,
      fadeMs
    });
    await this.stopCurrentBgm(normalizeDurationMs(fadeMs));
  }

  public fadeBgm(toVolume: number, durationMs: number): void {
    this.bgmVolume = clamp01(toVolume);
    this.persistSettings?.({ bgmVolume: this.bgmVolume });
    debugLogger.log('audio', 'fadeBgm', {
      currentBgmId: this.currentBgm?.id ?? null,
      bgmVolume: this.bgmVolume,
      durationMs
    });

    const bgm = this.currentBgm;
    if (!bgm?.howl || bgm.soundId === null) {
      return;
    }

    const duration = normalizeDurationMs(durationMs);
    if (duration <= 0) {
      this.applyCurrentBgmVolume();
      return;
    }

    bgm.howl.fade(
      getHowlVolume(bgm.howl, bgm.soundId),
      this.getBgmPlaybackVolume(bgm.id),
      duration,
      bgm.soundId
    );
  }

  public async playSe(id: string, options: PlaySeOptions = {}): Promise<AudioPlayResult> {
    this.seRequestCount += 1;
    this.lastSeRequestId = id;
    debugLogger.log('audio', 'playSe request', {
      id,
      requestCount: this.seRequestCount,
      options
    });
    const policy = resolveSePolicy(id, options);
    const now = getNowMs();
    const lastPlayedAt = this.lastSePlayedAt.get(id);
    if (
      policy.cooldownMs > 0 &&
      lastPlayedAt !== undefined &&
      now - lastPlayedAt < policy.cooldownMs
    ) {
      this.seSuppressedCount += 1;
      debugLogger.log('audio', 'playSe suppressed by cooldown', {
        id,
        cooldownMs: policy.cooldownMs,
        elapsedMs: Math.max(0, Math.round(now - lastPlayedAt))
      });
      return createSuppressedSeResult(id, 'cooldown');
    }

    const asset = await this.assetLoader.loadAudio({ id, kind: 'se' });
    if (asset.placeholder) {
      debugLogger.log('audio', 'playSe placeholder', { id, url: asset.url });
      return { id, channel: 'se', played: false, placeholder: true };
    }

    const howl = this.getOrCreateSe(id, asset.url);
    if (!policy.allowOverlap && howl.playing()) {
      this.seSuppressedCount += 1;
      debugLogger.log('audio', 'playSe suppressed by overlap', { id });
      return createSuppressedSeResult(id, 'overlap');
    }

    let soundId: number | string | null | undefined;
    try {
      soundId = howl.play();
    } catch {
      debugLogger.log('audio', 'playSe play threw', { id });
      return { id, channel: 'se', played: false, placeholder: false };
    }

    if (typeof soundId !== 'number') {
      debugLogger.log('audio', 'playSe play returned non numeric id', { id, soundId });
      return { id, channel: 'se', played: false, placeholder: false };
    }

    howl.volume(this.seVolume * policy.volume, soundId);
    this.lastSePlayedAt.set(id, now);
    this.sePlaybackCount += 1;
    this.lastSePlayedId = id;
    debugLogger.log('audio', 'playSe started', {
      id,
      soundId,
      volume: this.seVolume * policy.volume
    });

    return { id, channel: 'se', played: true, placeholder: false };
  }

  public setBgmVolume(volume: number): void {
    this.bgmVolume = clamp01(volume);
    this.persistSettings?.({ bgmVolume: this.bgmVolume });
    debugLogger.log('audio', 'setBgmVolume', { bgmVolume: this.bgmVolume });

    this.applyCurrentBgmVolume();
  }

  public setSeVolume(volume: number): void {
    this.seVolume = clamp01(volume);
    this.persistSettings?.({ seVolume: this.seVolume });
    debugLogger.log('audio', 'setSeVolume', { seVolume: this.seVolume });

    for (const [id, howl] of this.seCache) {
      howl.volume(this.getSePlaybackVolume(id));
    }
  }

  public setVoiceVolume(volume: number): void {
    this.voiceVolume = clamp01(volume);
    this.persistSettings?.({ voiceVolume: this.voiceVolume });
    debugLogger.log('audio', 'setVoiceVolume', { voiceVolume: this.voiceVolume });
  }

  public setMasterVolume(volume: number): void {
    this.masterVolume = clamp01(volume);
    this.persistSettings?.({ masterVolume: this.masterVolume });
    Howler.volume(this.masterVolume);
    this.applyCurrentBgmVolume();
    debugLogger.log('audio', 'setMasterVolume', { masterVolume: this.masterVolume });
  }

  public mute(muted = true): void {
    this.muted = muted;
    Howler.mute(muted);
    debugLogger.log('audio', 'mute', { muted });
  }

  public unlockFromUserGesture(source: AudioUnlockSource = 'debug'): AudioDebugState {
    this.audioUnlockAttemptCount += 1;
    this.lastAudioUnlockSource = source;
    const audioContextState = getHowlerAudioContextState();
    debugLogger.log('audio', 'user gesture audio unlock', {
      source,
      attemptCount: this.audioUnlockAttemptCount,
      audioContextState
    });

    if (audioContextState === 'suspended' || audioContextState === 'interrupted') {
      try {
        const resumePromise = Howler.ctx?.resume?.();
        this.audioContextResumeCount += 1;
        void Promise.resolve(resumePromise)
          .then(() => {
            this.retryPendingBgmUnlocks();
          })
          .catch(() => {
            debugLogger.log('audio', 'audio context resume rejected', { source });
          });
      } catch {
        debugLogger.log('audio', 'audio context resume threw', { source });
      }
    }

    this.retryPendingBgmUnlocks();
    return this.getDebugState();
  }

  public getDebugState(): AudioDebugState {
    return {
      currentBgmId: this.currentBgm?.id ?? null,
      currentBgmPlaceholder: this.currentBgm?.placeholder ?? false,
      muted: this.muted,
      audioUnlockAttemptCount: this.audioUnlockAttemptCount,
      audioContextResumeCount: this.audioContextResumeCount,
      audioContextState: getHowlerAudioContextState(),
      lastAudioUnlockSource: this.lastAudioUnlockSource,
      masterVolume: this.masterVolume,
      bgmVolume: this.bgmVolume,
      seVolume: this.seVolume,
      voiceVolume: this.voiceVolume,
      activeBgmHowlCount: this.bgmHowls.size,
      activeBgmPlayingCount: this.countActiveBgmPlayback(),
      bgmRequestCount: this.bgmRequestCount,
      bgmStartCount: this.bgmStartCount,
      bgmPlaceholderCount: this.bgmPlaceholderCount,
      bgmPlayErrorCount: this.bgmPlayErrorCount,
      bgmUnlockRetryCount: this.bgmUnlockRetryCount,
      lastBgmRequestId: this.lastBgmRequestId,
      lastBgmStartedId: this.lastBgmStartedId,
      lastBgmPlayErrorId: this.lastBgmPlayErrorId,
      cachedSeCount: this.seCache.size,
      seRequestCount: this.seRequestCount,
      sePlaybackCount: this.sePlaybackCount,
      seSuppressedCount: this.seSuppressedCount,
      lastSeRequestId: this.lastSeRequestId,
      lastSePlayedId: this.lastSePlayedId
    };
  }

  public destroy(): void {
    this.destroyed = true;
    this.bgmRequestToken += 1;

    for (const howl of this.bgmHowls) {
      stopAndUnloadHowl(howl, null);
    }
    this.bgmHowls.clear();
    this.pendingBgmUnlockRetries.clear();
    this.currentBgm = null;

    for (const howl of this.seCache.values()) {
      stopAndUnloadHowl(howl, null);
    }
    this.seCache.clear();
    this.lastSePlayedAt.clear();
    this.bgmRequestCount = 0;
    this.bgmStartCount = 0;
    this.bgmPlaceholderCount = 0;
    this.bgmPlayErrorCount = 0;
    this.bgmUnlockRetryCount = 0;
    this.audioUnlockAttemptCount = 0;
    this.audioContextResumeCount = 0;
    this.lastAudioUnlockSource = null;
    this.lastBgmRequestId = null;
    this.lastBgmStartedId = null;
    this.lastBgmPlayErrorId = null;
    this.seRequestCount = 0;
    this.sePlaybackCount = 0;
    this.seSuppressedCount = 0;
    this.lastSeRequestId = null;
    this.lastSePlayedId = null;

    this.muted = false;
    Howler.mute(false);
    Howler.volume(DEFAULT_MASTER_VOLUME);
    debugLogger.log('audio', 'destroy audio manager');
  }

  private async stopCurrentBgm(fadeMs: number): Promise<void> {
    const bgm = this.currentBgm;
    this.currentBgm = null;

    if (!bgm?.howl) {
      this.unloadOrphanBgmHowls();
      debugLogger.log('audio', 'stopCurrentBgm no active howl', {
        previousBgmId: bgm?.id ?? null,
        placeholder: bgm?.placeholder ?? false
      });
      return;
    }

    const { howl, soundId } = bgm;
    if (fadeMs <= 0 || soundId === null || !howl.playing(soundId)) {
      this.unloadBgmHowl(howl, soundId);
      debugLogger.log('audio', 'stopCurrentBgm immediate', { id: bgm.id, soundId, fadeMs });
      return;
    }

    await new Promise<void>((resolve) => {
      let settled = false;
      const cleanup = (): void => {
        if (settled) {
          return;
        }

        settled = true;
        globalThis.clearTimeout(timeoutId);
        this.unloadBgmHowl(howl, soundId);
        debugLogger.log('audio', 'stopCurrentBgm faded', { id: bgm.id, soundId, fadeMs });
        resolve();
      };

      const timeoutId = globalThis.setTimeout(cleanup, fadeMs + FADE_CLEANUP_GRACE_MS);

      howl.once('fade', cleanup, soundId);
      howl.fade(getHowlVolume(howl, soundId), 0, fadeMs, soundId);
    });
  }

  private getOrCreateSe(id: string, url: string): Howl {
    const cached = this.seCache.get(id);
    if (cached) {
      return cached;
    }

    const howl = new Howl({
      src: [url],
      html5: true,
      volume: this.seVolume
    });
    howl.once('loaderror', () => {
      this.seCache.delete(id);
      stopAndUnloadHowl(howl, null);
      debugLogger.log('audio', 'playSe loaderror', { id, url });
    });
    this.seCache.set(id, howl);
    return howl;
  }

  private bindBgmLoadError(id: string, howl: Howl, getSoundId: () => number | null): void {
    howl.once('loaderror', () => {
      if (!this.isCurrentBgm(howl)) {
        this.unloadBgmHowl(howl, getSoundId());
        return;
      }

      this.bgmPlaceholderCount += 1;
      this.currentBgm = {
        id,
        howl: null,
        soundId: null,
        placeholder: true
      };
      this.unloadBgmHowl(howl, getSoundId());
      debugLogger.log('audio', 'playBgm loaderror placeholder', { id });
    });
  }

  private bindBgmFadeIn(
    id: string,
    howl: Howl,
    getSoundId: () => number | null,
    fadeMs: number
  ): void {
    if (fadeMs <= 0) {
      return;
    }

    howl.once('play', (playedSoundId) => {
      if (!this.isCurrentBgm(howl) || playedSoundId !== getSoundId()) {
        return;
      }

      const targetVolume = this.getBgmPlaybackVolume(id);
      if (targetVolume <= 0) {
        stopHowlFade(howl, playedSoundId);
        howl.volume(0, playedSoundId);
        return;
      }

      howl.fade(0, targetVolume, fadeMs, playedSoundId);
    });
  }

  private applyCurrentBgmVolume(): void {
    const bgm = this.currentBgm;
    if (!bgm?.howl || bgm.soundId === null) {
      return;
    }

    stopHowlFade(bgm.howl, bgm.soundId);
    bgm.howl.volume(this.getBgmPlaybackVolume(bgm.id), bgm.soundId);
  }

  private getBgmPlaybackVolume(id: string): number {
    return this.bgmVolume * getTrackDefaultVolume(id);
  }

  private getSePlaybackVolume(id: string): number {
    return this.seVolume * getTrackDefaultVolume(id);
  }

  private countActiveBgmPlayback(): number {
    let activeCount = 0;

    for (const howl of this.bgmHowls) {
      try {
        if (howl.playing()) {
          activeCount += 1;
        }
      } catch {
        // Debug counters should never make audio state inspection fail.
      }
    }

    return activeCount;
  }

  private bindBgmAutoplayRetry(id: string, howl: Howl, getSoundId: () => number | null): void {
    howl.once('playerror', (failedSoundId) => {
      this.bgmPlayErrorCount += 1;
      this.lastBgmPlayErrorId = id;
      debugLogger.log('audio', 'playBgm playerror', { id, failedSoundId });
      const retry = (): void => {
        if (!this.pendingBgmUnlockRetries.has(howl)) {
          return;
        }

        if (!this.isCurrentBgm(howl)) {
          return;
        }

        const retrySoundId = typeof failedSoundId === 'number' ? failedSoundId : getSoundId();
        if (retrySoundId === null) {
          return;
        }

        let playedSoundId: number | string | null | undefined;
        try {
          playedSoundId = howl.play(retrySoundId);
        } catch {
          this.bgmPlayErrorCount += 1;
          this.lastBgmPlayErrorId = id;
          debugLogger.log('audio', 'playBgm unlock retry threw', { id, retrySoundId });
          return;
        }

        if (typeof playedSoundId !== 'number') {
          this.bgmPlayErrorCount += 1;
          this.lastBgmPlayErrorId = id;
          debugLogger.log('audio', 'playBgm unlock retry returned non numeric id', {
            id,
            playedSoundId
          });
          return;
        }

        this.pendingBgmUnlockRetries.delete(howl);
        this.bgmUnlockRetryCount += 1;
        this.bgmStartCount += 1;
        this.lastBgmStartedId = id;
        this.currentBgm = {
          id,
          howl,
          soundId: playedSoundId,
          placeholder: false
        };
        debugLogger.log('audio', 'playBgm unlock retry started', { id, playedSoundId });
      };

      this.pendingBgmUnlockRetries.set(howl, retry);
      howl.once('unlock', retry);
    });
  }

  private bindBgmEnd(
    id: string,
    howl: Howl,
    getSoundId: () => number | null,
    onEnd: ((id: string) => void) | undefined
  ): void {
    if (!onEnd) {
      return;
    }

    howl.once('end', (endedSoundId) => {
      const soundId = getSoundId();
      if (!this.isCurrentBgm(howl) || soundId === null) {
        return;
      }

      if (typeof endedSoundId === 'number' && endedSoundId !== soundId) {
        return;
      }

      onEnd(id);
    });
  }

  private retryPendingBgmUnlocks(): void {
    for (const retry of [...this.pendingBgmUnlockRetries.values()]) {
      retry();
    }
  }

  private canReuseCurrentBgm(bgm: ActiveBgm): boolean {
    if (!bgm.howl || bgm.soundId === null) {
      return false;
    }

    try {
      return bgm.howl.playing(bgm.soundId) || bgm.howl.state() === 'loading';
    } catch {
      return false;
    }
  }

  private isCurrentBgm(howl: Howl): boolean {
    return !this.destroyed && this.currentBgm?.howl === howl;
  }

  private isLatestBgmRequest(requestToken: number): boolean {
    return !this.destroyed && requestToken === this.bgmRequestToken;
  }

  private unloadBgmHowl(howl: Howl, soundId: number | null): void {
    if (!this.bgmHowls.delete(howl)) {
      return;
    }

    this.pendingBgmUnlockRetries.delete(howl);
    stopAndUnloadHowl(howl, soundId);
  }

  private unloadOrphanBgmHowls(): void {
    for (const howl of [...this.bgmHowls]) {
      this.unloadBgmHowl(howl, null);
    }
  }
}

function stopAndUnloadHowl(howl: Howl, soundId: number | null): void {
  howl.off();
  if (soundId === null) {
    howl.stop();
  } else {
    howl.stop(soundId);
  }
  howl.unload();
}

function stopHowlFade(howl: Howl, soundId: number): void {
  const stopFade = (howl as HowlWithStopFade)._stopFade;
  if (typeof stopFade !== 'function') {
    return;
  }

  try {
    stopFade.call(howl, soundId);
  } catch {
    // Howler's fade internals are best-effort; the following explicit volume call is authoritative.
  }
}

function getHowlVolume(howl: Howl, soundId: number): number {
  const volume = howl.volume(soundId);
  return typeof volume === 'number' ? volume : DEFAULT_MASTER_VOLUME;
}

function createInterruptedBgmResult(id: string): AudioPlayResult {
  return { id, channel: 'bgm', played: false, placeholder: false, interrupted: true };
}

function createSuppressedSeResult(
  id: string,
  suppressedReason: NonNullable<AudioPlayResult['suppressedReason']>
): AudioPlayResult {
  return {
    id,
    channel: 'se',
    played: false,
    placeholder: false,
    suppressed: true,
    suppressedReason
  };
}

function resolveSePolicy(id: string, options: PlaySeOptions): SePlaybackPolicy {
  const basePolicy = SE_POLICIES[id] ?? {};
  const baseVolume = basePolicy.volume ?? getTrackDefaultVolume(id);
  const optionVolumeMultiplier = options.volume ?? 1;
  return {
    volume: clamp01(baseVolume * optionVolumeMultiplier),
    cooldownMs: normalizeDurationMs(
      options.cooldownMs ?? basePolicy.cooldownMs ?? DEFAULT_SE_POLICY.cooldownMs
    ),
    allowOverlap: options.allowOverlap ?? basePolicy.allowOverlap ?? DEFAULT_SE_POLICY.allowOverlap
  };
}

function getNowMs(): number {
  return globalThis.performance?.now() ?? Date.now();
}

function getTrackDefaultVolume(id: string): number {
  return AUDIO_DEFAULT_VOLUMES.get(id) ?? DEFAULT_MASTER_VOLUME;
}

function getHowlerAudioContextState(): string | null {
  try {
    return Howler.ctx?.state ?? null;
  } catch {
    return null;
  }
}

function normalizeAudioDefaultVolume(value: unknown): number {
  return typeof value === 'number' ? clamp01(value) : DEFAULT_MASTER_VOLUME;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.min(Math.max(value, 0), 1);
}

function normalizeDurationMs(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  return Math.max(0, value);
}
